import { customColors } from '@apollo/orbit';
import classnames from 'classnames';
import { LocationDescriptor } from 'history';
import { kebabCase } from 'lodash';
import * as React from 'react';
import { NavLink } from 'react-router-dom';

import { PlainHtmlButton } from '../plainHtmlButton/PlainHtmlButton';

export interface CommonProps {
  tabID: string;
  tabName?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  badge?: React.ReactNode;
}

/**
 * Provide either `to` or `onClick`.
 * The `isActive` prop is optional for `to` but required for `onClick`.
 */
export type LinkProps =
  | { onClick?: never; isActive?: () => boolean; to: LocationDescriptor }
  | { onClick: () => void; isActive: () => boolean; to?: never };

/**
 * Renders a clickable tab element.  If onClick is passed, renders a button,
 * otherwise if active, renders a link.
 */
export const HeaderTab = React.forwardRef<HTMLElement, CommonProps & LinkProps>(
  (
    { tabID, tabName, badge, disabled, className, isActive, onClick, to },
    ref,
  ) => {
    if (disabled) {
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          className={classnames(
            /**
             * We use pb-[14px] here to compensate for an old border-b-2 that made
             * the bottom distance specifically 14px (between pb-3 and pb-4). It
             * would be nice get this back to regular TW sizing one day, but these
             * header tabs exist all across the app and that would change a lot of
             * pages.
             */
            'mr-4 inline-block cursor-default whitespace-nowrap pb-[14px] pt-2 text-base text-disabled focus:rounded-md focus-visible:ring-2',
            className,
          )}
        >
          {tabName || tabID}
          {badge}
        </div>
      );
    }

    if (onClick) {
      return (
        <PlainHtmlButton
          ref={ref as React.Ref<HTMLButtonElement>}
          onClick={onClick}
          className={classnames(
            /**
             * We use pb-[10px] here to compensate for an old border-b-2 that made
             * the bottom distance specifically 14px (between pb-2 and pb-3). It
             * would be nice get this back to regular TW sizing one day, but these
             * header tabs exist all across the app and that would change a lot of
             * pages.
             */
            'mr-4 inline-block whitespace-nowrap px-2 pb-[10px] pt-2 text-base focus:rounded-md focus-visible:ring-2',
            isActive && isActive()
              ? 'active-tab-decorator relative font-semibold text-primary'
              : 'font-normal text-secondary hover:text-primary',
            className,
          )}
        >
          {tabName || tabID}
          {badge}
        </PlainHtmlButton>
      );
    }

    if (to)
      return (
        <NavLink
          ref={ref as React.Ref<HTMLAnchorElement>}
          data-testid={`${kebabCase(tabID)}-tab`}
          to={to}
          isActive={isActive}
          /**
           * We use pb-[14px] here to compensate for an old border-b-2 that made
           * the bottom distance specifically 14px (between pb-3 and pb-4). It
           * would be nice get this back to regular TW sizing one day, but these
           * header tabs exist all across the app and that would change a lot of
           * pages.
           */
          className="mr-6 inline-block whitespace-nowrap pb-[14px] pt-2 text-base text-secondary hover:text-primary focus:rounded-md focus-visible:ring-2"
          activeClassName="font-semibold active-tab-decorator relative"
          activeStyle={{ color: customColors.text.primary }}
        >
          {tabName || tabID}
          {badge}
        </NavLink>
      );

    return null;
  },
);
